var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { loadScript } from '../../utils';
/**
 *  Seats.io service
 */
export default class SeatsioService {
    /**
     *  Construct the Seats.io service
     *
     *  This will lock the config in place for all future use cases.
     */
    constructor(config) {
        this.config = this._ensureConfig(config);
    }
    /**
     *  Ensure the Seats.io chart library is loaded.
     */
    ensureLoaded() {
        return __awaiter(this, void 0, void 0, function* () {
            if (!window.seatsio) {
                yield loadScript('https://cdn.seatsio.net/chart.js');
            }
            if (!window.seatsio) {
                throw new Error('Seats.io -> Not loaded');
            }
        });
    }
    /**
     *  Mount the SeatingChart to the DOM.
     */
    mount(options) {
        return __awaiter(this, void 0, void 0, function* () {
            yield this.ensureLoaded();
            this.destroy();
            const config = this._ensureOptions(options);
            // Save some current callbacks.
            // These callbacks can now be wrapped by internal functionality.
            const onChartRendered = config.onChartRendered;
            const onChartRenderingFailed = config.onChartRenderingFailed;
            const onHoldTokenExpired = config.onHoldTokenExpired;
            // Create a promise that will resolve when the chart has been rendered
            // or reject if it fails rendering.
            const rendered = new Promise((resolve, reject) => {
                config.onChartRendered = (chart) => {
                    this._setHoldToken();
                    onChartRendered && onChartRendered(chart);
                    resolve(this);
                };
                config.onChartRenderingFailed = (chart) => {
                    onChartRenderingFailed && onChartRenderingFailed(chart);
                    this.chart = undefined;
                    chart.destroy();
                    reject();
                };
                config.onHoldTokenExpired = () => {
                    this.holdToken = undefined;
                    onHoldTokenExpired && onHoldTokenExpired();
                    if (this.chart) {
                        this.chart.startNewSession(this._setHoldToken.bind(this), () => {
                            throw new Error('Seats.io -> Failed to start new seatsio session');
                        });
                    }
                    else {
                        console.error('Failed to start new seatsio session -> no chart');
                    }
                };
            });
            this.chart = new window.seatsio.SeatingChart(config);
            this.chart.render();
            return rendered;
        });
    }
    /**
     *  Destroy the current seating chart
     *
     *  This is a mandatory step. Failing to destroy charts will lead to memory leaks!
     */
    destroy() {
        if (!this.chart) {
            return;
        }
        const chart = this.chart;
        this.chart = undefined;
        chart.destroy();
    }
    /**
     *  Validate the config and merge with the current (default) config.
     */
    _ensureConfig(config) {
        const conf = Object.assign(Object.assign({}, this.config), config);
        if (!conf.workspaceKey) {
            throw new Error('Seats.io -> Workspace key missing');
        }
        return this.config = conf;
    }
    /**
     *  Validate the options (config) and ensure default values are set.
     */
    _ensureOptions(options) {
        if (!options.divId || options.divId.length < 2 || !options.divId.startsWith('#')) {
            throw new Error('Seats.io -> Invalid divId, expected DOM id selector (starts with #)');
        }
        if (!options.events || !options.events.length) {
            throw new Error('Seats.io -> Expected at least one event key');
        }
        if (!options.pricing || !options.pricing.length) {
            throw new Error('Seats.io -> Invalid pricing, expected at least one pricing option');
        }
        const selectionValidators = [];
        if (!options.allowOrphanSeats) {
            selectionValidators.push({
                type: 'noOrphanSeats',
                mode: 'strict',
            });
        }
        if (options.forceConsecutiveSeats) {
            selectionValidators.push({
                type: 'consecutiveSeats',
            });
        }
        return Object.assign({ 
            // DOM location
            divId: options.divId.replace(/^[#]/, ''), 
            // Session values
            session: this.holdToken ? 'continue' : 'start', 
            // Note: holdOnSelect is not allowed when passing in session
            // holdOnSelect: true,
            holdOnSelectForGAs: true, 
            // Chart, event, category, pricing settings
            events: options.events, pricing: options.pricing, maxSelectedObjects: options.maxSelectedObjects, priceFormatter: options.priceFormatter, objectWithoutPricingSelectable: false, objectWithoutCategorySelectable: false, canGASelectionBeIncreased: undefined, 
            // Explicitly disabled! Could potentially trigger large number of concurrent reservations!
            multiSelectEnabled: false, availableCategories: undefined, unavailableCategories: undefined, selectedObjects: undefined, 
            // Validators
            selectionValidators, 
            // Styling
            showRowLines: true, showLegend: options.showLegend !== false, legend: {
                hideNonSelectableCategories: true,
            }, language: ensureLanguage(options.language), showMinimap: true, loading: options.loading || undefined, colorScheme: options.colorScheme || 'light', colors: {
                // The color used for certain interactive, selected or highlighted user interface elements.
                colorSelected: (options.colors && options.colors.colorSelected) || undefined,
                //The background color for the whole tooltip.
                cursorTooltipBackgroundColor: (options.colors && options.colors.cursorTooltipBackgroundColor) || undefined,
                // The color of certain tiles, such as in modal dialogs or the Category Filter.
                colorTitle: (options.colors && options.colors.colorTitle) || undefined,
            }, stylePreset: options.stylePreset || 'balance', style: options.style || undefined, onHoldSucceeded: options.onHoldSucceeded || undefined, onHoldFailed: options.onHoldFailed || undefined, onHoldTokenExpired: options.onHoldTokenExpired || undefined, onReleaseHoldSucceeded: options.onReleaseHoldSucceeded || undefined, onReleaseHoldFailed: options.onReleaseHoldFailed || undefined, onSelectionValid: options.onSelectionValid || undefined, onSelectionInvalid: options.onSelectionInvalid || undefined, 
            // Non-exposed events.
            // For now the below events are not exposed to any implementor.
            // Should be added when needed!
            // onChartRendered
            // onChartRenderingFailed
            // onObjectClicked
            // onObjectSelected
            // onObjectDeselected
            // onObjectMouseOver
            // onObjectMouseOut
            // onSelectedObjectBooked
            // onBestAvailableSelected
            // onBestAvailableSelectionFailed
            // onFullScreenOpened
            // onFullScreenClosed
            // Other
            unifiedObjectPropertiesInCallbacks: true, extraConfig: undefined }, this.config);
    }
    /**
     *  Set the hold token, this signals the Seats.io class a session is currently active.
     */
    _setHoldToken() {
        if (!this.chart) {
            console.error('Failed to retrieve hold token -> chart does not exist');
        }
        else if (this.chart.holdToken) {
            this.holdToken = this.chart.holdToken;
        }
        else {
            console.error('Failed to retrieve hold token -> Session not started');
        }
    }
}
/**
 *  Process a language or locale to be a supported language string or fallback to the given default.
 */
function ensureLanguage(lang, defaultLang = 'en') {
    if (!lang) {
        return defaultLang;
    }
    if (/^[a-z]{2}_/.test(lang)) {
        lang = lang.substr(0, 2);
    }
    // See: https://support.seats.io/en/articles/2074430-translating-embedded-floor-plans-i18n
    const languages = {
        'ar': 'Arabic',
        'be': 'Belarusian',
        'bg': 'Bulgarian',
        'ca': 'Catalan',
        'cs': 'Czech',
        'da': 'Danish',
        'de': 'German',
        'el': 'Greek',
        'en': 'English',
        'es': 'Spanish',
        'et': 'Estonian',
        'fa': 'Persian (Farsi)',
        'fi': 'Finnish',
        'fr': 'French',
        'hr': 'Croatian',
        'he': 'Hebrew',
        'hu': 'Hungarian',
        'it': 'Italian',
        'ja': 'Japanese',
        'li': 'Lithuanian',
        'lv': 'Latvian',
        'no': 'Norwegian',
        'nl': 'Dutch',
        'pl': 'Polish',
        'pt': 'Portuguese',
        'ro': 'Romanian',
        'ru': 'Russian',
        'sk': 'Slovak',
        'sl': 'Slovenian',
        'sr': 'Serbian',
        'sv': 'Swedish',
        'tr': 'Turkish',
        'uk': 'Ukrainian',
        'zh-Hans': 'Simplified Chinese',
        'zh-Hant': 'Traditional Chinese',
    };
    return languages[lang] ? lang : defaultLang;
}
//# sourceMappingURL=seatsio.js.map